<template>
  <q-card
    bordered
    flat
    data-test-id="access-level-summary-card"
  >
    <q-card-section
      class="q-px-none"
      horizontal
    >
      <q-card-section class="col q-pb-none">
        <div
          class="text-h6"
          style="font-weight: normal;"
        >
          Access Level
        </div>
      </q-card-section>
      <q-card-section class="col-auto q-pb-none">
        <q-btn
          v-if="isPermittedToChangeUserAccess"
          outline
          color="negative"
          @click="showUpdateAccessLevelDialog = true"
        >
          Change Access Level
        </q-btn>
      </q-card-section>
    </q-card-section>
    <q-card-section class="q-pt-none">
      <div class="row">
        <div class="col col-md-9">
          <!-- Admin -->
          <template v-if="user.admin">
            <div class="text-subtitle1">
              {{ user.circuitAuthorized ? 'Circuit' : 'Site' }} Admin
            </div>
            <div class="text-grey-10">
              This user is an Admin and has access to all admin and employee apps for {{ user.circuitAuthorized ? 'all of the current circuit\'s sites' : 'the following authorized sites only' }}.
              <br>The user is treated as a Non-Employee on unauthorized sites.
            </div>
          </template>

          <!-- Employee -->
          <template v-else-if="user.employee">
            <div class="text-subtitle1">
              {{ user.circuitAuthorized ? 'Circuit' : 'Site' }} Employee
            </div>
            <div class="text-grey-10">
              This user is an Employee and has access to employee apps, including Point of Sale and Kitchen Display System, for {{ user.circuitAuthorized ? 'all of the current circuit\'s Sites' : 'the following authorized sites only' }}.
              <br>The user does not have access to the admin app.
              <br>The user is treated as a Non-Employee on unauthorized sites.
            </div>
          </template>

          <!-- Non-Employee -->
          <template v-else>
            <div class="text-subtitle1">
              Non-Employee
            </div>
            <div class="text-grey-10">
              This user is a Non-Employee and does not have access to any admin or employee apps.
            </div>
          </template>
        </div>
      </div>

      <!-- Authorized sites of current circuit -->
      <div
        v-if="user.admin || user.employee"
        class="q-mt-md"
      >
        <div v-if="!user.circuitAuthorized">
          <div class="text-subtitle1">
            On Specific <em>{{ currentCircuit.name }}</em> Sites
          </div>
          <div class="text-grey-10">
            The user has {{ user.admin ? 'admin and employee' : 'employee' }} access to the following <em>{{ currentCircuit.name }}</em> sites only:
          </div>
          <q-chip
            v-for="site in currentAuthorizedSitesOfCurrentCircuit"
            :key="site.id"
            dense
          >
            {{ site.name }}
          </q-chip>
        </div>
        <div v-else-if="user.circuitAuthorized">
          <div class="text-subtitle1">
            On All <em>{{ currentCircuit.name }}</em> Sites
          </div>
          <div class="text-grey-10">
            The user has {{ user.admin ? 'admin and employee' : 'employee' }} access to all <strong>current and future</strong>&nbsp;<em>{{ currentCircuit.name }}</em> sites:
          </div>
          <q-chip
            v-for="site in currentUser.libraryAndAuthorizedSiteOptions"
            :key="site.id"
            dense
          >
            {{ site.name }}
          </q-chip>
        </div>
      </div>

      <!-- Authorized sites of other circuits -->
      <div
        v-if="currentAuthorizedSitesOfOtherCircuits.length"
        class="q-mt-md"
      >
        <div class="text-subtitle1">
          On Other Circuit Sites
        </div>
        <div class="text-grey-10">
          The {{ user.admin ? 'admin' : 'employee' }} user also has access to the following specific sites from other circuits:
        </div>
        <q-chip
          v-for="site in currentAuthorizedSitesOfOtherCircuits"
          :key="site.id"
          dense
        >
          {{ site.name }}
        </q-chip>
      </div>
    </q-card-section>

    <update-access-level-dialog
      v-model="showUpdateAccessLevelDialog"
      :user="user"
      @save="$emit('save')"
    />
  </q-card>
</template>

<script>
import UpdateAccessLevelDialog from 'src/modules/admin/user/UserForm/components/UpdateAccessLevelDialog'

export default {
  name: 'AccessLevelSummaryCard',
  components: {
    UpdateAccessLevelDialog
  },
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['save'],
  data () {
    return {
      showUpdateAccessLevelDialog: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit'],
  computed: {
    isPermittedToChangeUserAccess () {
      if (this.user.admin) {
        return this.$isPermitted(this.$permissions.Security.PromoteUserToAdminOrEmployee)
      } else {
        return this.$isAnyPermitted(this.$permissions.Security.PromoteUserToEmployee, this.$permissions.Security.PromoteUserToAdminOrEmployee)
      }
    },
    currentAuthorizedSitesOfCurrentCircuit () {
      return _(this.user.authorizedSites?.data)
        .filter(site => site.circuitId === this.currentCircuit.id)
        .sortBy('name')
        .value()
    },
    currentAuthorizedSitesOfOtherCircuits () {
      return _(this.user.authorizedSites.data)
        .filter(site => site.circuitId !== this.currentCircuit.id)
        .sortBy('name')
        .value()
    }
  }
}
</script>
